// ─────────────────────────────────────────────────────────────────────────────
// usePageContext – DOM / API Context Collection Hook (ESG-only)
// Reads the current page's DOM, intercepts API calls, detects ESG signals,
// and generates pre-populated prompt suggestions.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import type {
	PageContext,
	DOMContext,
	TableData,
	FormFieldData,
	ESGContext,
	ESGDomain,
} from "../types";
import { widgetRegistry } from "../registry/widgetRegistry";
import { wrapUntrusted, UNTRUSTED_PREAMBLE } from "../security";

// ── Period / Entity Detection (shared) ────────────────────────────────────────

function detectPeriod(text: string): string {
	const yearMatch = text.match(/(?:FY|fiscal year|year ended|as of|period ended)[\s:]*(\d{4})/i);
	if (yearMatch) return yearMatch[1];
	const dateMatch = text.match(
		/(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/i
	);
	if (dateMatch) return dateMatch[0];
	const yearOnly = text.match(/\b(20\d{2})\b/);
	if (yearOnly) return yearOnly[1];
	return "";
}

function detectEntity(text: string): string {
	const match = text.match(
		/^([A-Z][A-Za-z\s,\.&]+(?:Inc\.?|Corp\.?|LLC\.?|Ltd\.?|Co\.?|Group|Holdings)?)/m
	);
	return match ? match[1].trim() : "";
}

// ── ESG Pattern Detection ─────────────────────────────────────────────────────

const ESG_FRAMEWORK_KEYWORDS = [
	"gri",
	"tcfd",
	"sasb",
	"csrd",
	"esrs",
	"ifrs s1",
	"ifrs s2",
	"esg",
	"csr",
	"sustainability report",
	"sdg",
	"science based targets",
	"sbt",
	"re100",
	"net zero",
	"carbon neutral",
];
const ENVIRONMENTAL_KEYWORDS = [
	"emissions",
	"ghg",
	"greenhouse gas",
	"scope 1",
	"scope 2",
	"scope 3",
	"carbon",
	"climate",
	"renewable energy",
	"water withdrawal",
	"water consumption",
	"waste",
	"biodiversity",
	"deforestation",
	"pollution",
	"co2",
	"methane",
	"energy intensity",
	"carbon footprint",
];
const SOCIAL_KEYWORDS = [
	"diversity",
	"inclusion",
	"dei",
	"gender pay",
	"gender gap",
	"trir",
	"incident rate",
	"safety",
	"employee turnover",
	"human rights",
	"labor",
	"community investment",
	"training hours",
	"wellbeing",
	"parental leave",
	"supply chain labor",
	"modern slavery",
];
const GOVERNANCE_ESG_KEYWORDS = [
	"board composition",
	"board diversity",
	"executive compensation",
	"pay ratio",
	"shareholder rights",
	"ethics",
	"anti-corruption",
	"whistleblower",
	"data privacy",
	"tax transparency",
	"stakeholder engagement",
	"materiality",
];

function countKeywordMatches(text: string, keywords: string[]): number {
	const lower = text.toLowerCase();
	return keywords.reduce((count, kw) => {
		// Use word-boundary regex so short acronyms don't match mid-word substrings.
		// e.g. 'gri' must NOT match "integrity".
		const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const re = new RegExp(`\\b${escaped}\\b`, "i");
		return count + (re.test(lower) ? 1 : 0);
	}, 0);
}

function detectESGFrameworks(text: string): string[] {
	const lower = text.toLowerCase();
	const detected: string[] = [];
	if (lower.includes("gri")) detected.push("GRI");
	if (lower.includes("tcfd") || lower.includes("task force on climate")) detected.push("TCFD");
	if (lower.includes("sasb")) detected.push("SASB");
	if (lower.includes("csrd") || lower.includes("esrs")) detected.push("CSRD/ESRS");
	if (lower.includes("ifrs s1") || lower.includes("ifrs s2") || lower.includes("issb"))
		detected.push("IFRS S");
	if (lower.includes("science based target") || lower.includes("sbt")) detected.push("SBTi");
	if (lower.includes("un sdg") || lower.includes("sustainable development goal"))
		detected.push("SDGs");
	return detected;
}

function detectESGTopics(text: string): string[] {
	const lower = text.toLowerCase();
	const topics: string[] = [];
	if (lower.includes("emission") || lower.includes("ghg") || lower.includes("carbon"))
		topics.push("GHG Emissions");
	if (lower.includes("renewable energy") || lower.includes("energy intensity"))
		topics.push("Energy");
	if (lower.includes("water")) topics.push("Water");
	if (lower.includes("waste") || lower.includes("circular")) topics.push("Waste");
	if (lower.includes("diversity") || lower.includes("inclusion") || lower.includes("gender"))
		topics.push("Diversity & Inclusion");
	if (lower.includes("safety") || lower.includes("trir") || lower.includes("incident"))
		topics.push("OHS");
	if (lower.includes("climate") || lower.includes("net zero") || lower.includes("scope"))
		topics.push("Climate");
	if (lower.includes("human rights") || lower.includes("labor") || lower.includes("supply chain"))
		topics.push("Human Rights");
	return topics;
}

function analyzeESGContext(
	tables: TableData[],
	headings: string[],
	fullText: string
): ESGContext | null {
	const combined = [...headings, fullText].join(" ");

	const esgFrameworkHits = countKeywordMatches(combined, ESG_FRAMEWORK_KEYWORDS);
	const envHits = countKeywordMatches(combined, ENVIRONMENTAL_KEYWORDS);
	const socHits = countKeywordMatches(combined, SOCIAL_KEYWORDS);
	const govHits = countKeywordMatches(combined, GOVERNANCE_ESG_KEYWORDS);
	const totalHits = esgFrameworkHits + envHits + socHits + govHits;

	if (totalHits < 2) return null;

	// Determine dominant ESG domain
	let domain: ESGDomain = "esg_combined";
	if (envHits > socHits && envHits > govHits && esgFrameworkHits < 2) {
		domain = combined.toLowerCase().includes("climate") ? "climate" : "environmental";
	} else if (socHits > envHits && socHits > govHits && esgFrameworkHits < 2) {
		domain = "social";
	} else if (govHits > envHits && govHits > socHits && esgFrameworkHits < 2) {
		domain = "governance_esg";
	}

	return {
		domain,
		frameworks: detectESGFrameworks(combined),
		topics: detectESGTopics(combined),
		entity: detectEntity(combined),
		period: detectPeriod(combined),
	};
}

// ── DOM Extraction ────────────────────────────────────────────────────────────

function extractTables(root: Element): TableData[] {
	const tables: TableData[] = [];
	root.querySelectorAll("table").forEach((table) => {
		// Skip any table that lives inside the AI assistant widget
		if (table.closest("[data-cai-widget]")) return;
		const headers: string[] = [];
		const rows: string[][] = [];
		const caption = table.querySelector("caption")?.textContent?.trim();

		// Extract headers from <thead> or first <tr>
		const headerRow = table.querySelector("thead tr") ?? table.querySelector("tr");
		if (headerRow) {
			headerRow.querySelectorAll("th, td").forEach((cell) => {
				headers.push(cell.textContent?.trim() ?? "");
			});
		}

		// Extract data rows
		table.querySelectorAll("tbody tr, tr").forEach((row, idx) => {
			if (idx === 0 && !table.querySelector("thead")) return; // skip header row
			const cells: string[] = [];
			row.querySelectorAll("td, th").forEach((cell) => {
				cells.push(cell.textContent?.trim() ?? "");
			});
			if (cells.some((c) => c)) rows.push(cells);
		});

		if (headers.length > 0 || rows.length > 0) {
			tables.push({
				headers,
				rows,
				caption,
				id: table.id || undefined,
			});
		}
	});
	return tables;
}

function extractForms(root: Element): FormFieldData[][] {
	const forms: FormFieldData[][] = [];
	root.querySelectorAll("form").forEach((form) => {
		if (form.closest("[data-cai-widget]")) return;
		const fields: FormFieldData[] = [];
		form.querySelectorAll("input, select, textarea").forEach((field) => {
			const input = field as HTMLInputElement;
			if (["password", "hidden", "submit", "button", "reset"].includes(input.type)) return;
			const label =
				document.querySelector(`label[for="${input.id}"]`)?.textContent?.trim() ??
				input.getAttribute("aria-label") ??
				input.getAttribute("placeholder") ??
				input.name ??
				"";
			fields.push({
				name: input.name,
				label,
				value: input.value,
				type: input.type || input.tagName.toLowerCase(),
			});
		});
		if (fields.length > 0) forms.push(fields);
	});
	return forms;
}

function extractHeadings(root: Element): string[] {
	const headings: string[] = [];
	root.querySelectorAll("h1, h2, h3, h4").forEach((h) => {
		if (h.closest("[data-cai-widget]")) return;
		const text = h.textContent?.trim();
		if (text) headings.push(text);
	});
	return headings.slice(0, 20); // cap
}

function extractMeta(): Record<string, string> {
	const meta: Record<string, string> = {};
	document.querySelectorAll("meta[name], meta[property]").forEach((el) => {
		const key = el.getAttribute("name") ?? el.getAttribute("property") ?? "";
		const value = el.getAttribute("content") ?? "";
		if (key && value) meta[key] = value;
	});
	return meta;
}

function extractJsonLd(): unknown[] {
	const results: unknown[] = [];
	document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
		try {
			results.push(JSON.parse(script.textContent ?? "{}"));
		} catch {
			// ignore malformed JSON-LD
		}
	});
	return results;
}

function extractTextSummary(root: Element, maxLength = 3000): string {
	// Remove script/style content and the AI assistant widget itself
	// (the widget renders ESG keywords in its own UI — e.g. "GRI 305", "TCFD"
	//  in the EmptyState tags, or full ESG prompt text — which would poison
	//  context detection on other tabs if not excluded).
	const clone = root.cloneNode(true) as Element;
	clone
		.querySelectorAll(
			'script, style, nav, footer, header, .menu, .sidebar, [role="navigation"], [data-cai-widget], [data-cai-context-exclude]'
		)
		.forEach((el) => el.remove());

	const text = clone.textContent ?? "";
	const cleaned = text.replace(/\s+/g, " ").trim();
	return cleaned.slice(0, maxLength);
}

// ── Suggested Prompts ─────────────────────────────────────────────────────────

function generateSuggestedPrompts(
	esg: ESGContext | null,
	domContext: DOMContext
): string[] {
	const prompts: string[] = [];

	if (esg) {
		const entityLabel = esg.entity || "this organization";
		const periodLabel = esg.period ? `for ${esg.period}` : "";
		const fwLabel = esg.frameworks.length > 0 ? esg.frameworks.join("/") : "GRI/TCFD";

		switch (esg.domain) {
			case "environmental":
			case "climate":
				prompts.push(
					`Analyze the GHG emissions and climate risk data for ${entityLabel} ${periodLabel} against ${fwLabel} requirements.`
				);
				prompts.push(
					"Calculate GHG intensity, renewable energy percentage, and assess progress toward net-zero targets."
				);
				prompts.push(
					"Review TCFD climate scenario analysis: what transition and physical risks are disclosed?"
				);
				break;
			case "social":
				prompts.push(
					`Evaluate the social performance metrics for ${entityLabel} ${periodLabel}: TRIR, turnover, gender pay gap, diversity.`
				);
				prompts.push(
					"Check GRI 401, 403, 405 disclosure completeness for employment and safety data shown here."
				);
				prompts.push(
					"Benchmark the diversity and inclusion metrics against GRI standards and regulatory requirements."
				);
				break;
			case "governance_esg":
				prompts.push(
					`Assess the ESG governance structure for ${entityLabel}: board composition, executive pay, ethics policies.`
				);
				prompts.push(
					"Review board diversity metrics and executive compensation disclosure against GRI 405 and proxy advisory guidance."
				);
				break;
			default: // esg_combined
				prompts.push(
					`Provide a comprehensive ESG analysis for ${entityLabel} ${periodLabel} covering Environmental, Social, and Governance pillars.`
				);
				prompts.push(
					`Check this ESG disclosure for completeness against ${fwLabel} framework requirements.`
				);
				prompts.push(
					"Identify any material ESG risks and opportunities not adequately disclosed here."
				);
		}
		prompts.push(
			"Search for recent ESG regulatory updates (CSRD, SEC climate rules, ISSB) that apply to this data."
		);
	}

	// ── Generic fallback (tables exist but no ESG context detected) ─────────
	if (!esg) {
		if (domContext.tables.length > 0) {
			prompts.push("Summarize the key data points from the tables on this page.");
			if (domContext.forms.length > 0) {
				prompts.push(
					"What does this form capture, and how does it relate to the workflow?"
				);
			}
		}
	}
	// If no context at all: return [] — component renders nothing

	return prompts.slice(0, 6); // Return top 6 suggestions
}

// (Global fetch/XHR interceptors removed in PR #8 — the WidgetRegistry is the
// authoritative source of on-screen data. Patching window.fetch globally was
// a privacy/PII liability and competed with other code that wraps fetch.)

// ── DOM Fingerprint ───────────────────────────────────────────────────────────
// Quick signature of the page's tables so we can detect meaningful content
// changes (e.g. tab switches) without re-reading on every minor DOM mutation
// (like chat bubbles being added).

function domFingerprint(root: Element): string {
	// Exclude AI widget content so chat bubble additions don't trigger false tab-switch
	const tables = Array.from(root.querySelectorAll("table")).filter(
		(t) => !t.closest("[data-cai-widget]")
	);
	const headings = Array.from(root.querySelectorAll("h1,h2,h3,h4")).filter(
		(h) => !h.closest("[data-cai-widget]")
	);
	let sig = `t:${tables.length}|h:${headings.length}`;
	tables.forEach((t) => {
		sig += `|${(t.textContent ?? "").length}`;
	});
	headings.forEach((h) => {
		sig += `|${h.textContent?.trim() ?? ""}`;
	});
	return sig;
}

// ── Main Hook ─────────────────────────────────────────────────────────────────

export interface UsePageContextOptions {
	/** @deprecated retained for prop compatibility — interceptors removed in PR #8 */
	includeApiInterception?: boolean;
	contextSelector?: string;
	maxContextLength?: number;
	/** @deprecated retained for prop compatibility */
	maxApiResponses?: number;
}

export function usePageContext(options: UsePageContextOptions = {}) {
	const {
		contextSelector = "body",
		maxContextLength = 4000,
	} = options;

	const [context, setContext] = useState<PageContext | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const buildContext = useCallback(() => {
		const root = document.querySelector(contextSelector) ?? document.body;

		const tables = extractTables(root);
		const forms = extractForms(root);
		const headings = extractHeadings(root);
		const textSummary = extractTextSummary(root, maxContextLength);
		const meta = extractMeta();
		const jsonLd = extractJsonLd();
		const description =
			(document.querySelector('meta[name="description"]') as HTMLMetaElement | null)
				?.content ?? "";

		const domContext: DOMContext = {
			url: window.location.href,
			title: document.title,
			description,
			tables,
			forms,
			headings,
			textSummary,
			jsonLd,
			meta,
		};

		const esg = analyzeESGContext(tables, headings, textSummary);
		const suggestedPrompts = generateSuggestedPrompts(esg, domContext);
		const widgetIndex = widgetRegistry.getIndex();

		const ctx: PageContext = {
			dom: domContext,
			esg,
			apiData: [],
			widgetIndex,
			suggestedPrompts,
			detectedAt: Date.now(),
		};

		setContext(ctx);
		setIsLoading(false);
		return ctx;
	}, [contextSelector, maxContextLength]);

	useEffect(() => {
		const root = document.querySelector(contextSelector) ?? document.body;

		// ── Initial context build (after DOM settles) ─────────────────────────────
		const timer = setTimeout(() => {
			buildContext();
		}, 500);

		// ── MutationObserver: re-read when tab content changes ────────────────────
		// We use a fingerprint (table count + heading text) to ignore minor DOM
		// changes (e.g. chat messages being appended) and only re-read when the
		// meaningful page content actually changes (e.g. switching tabs).
		let lastFingerprint = "";
		let debounceTimer: ReturnType<typeof setTimeout> | null = null;

		const observer = new MutationObserver(() => {
			const current = domFingerprint(root);
			if (current === lastFingerprint) return; // no meaningful content change
			lastFingerprint = current;

			if (debounceTimer) clearTimeout(debounceTimer);
			// 400 ms debounce – waits for React to finish rendering the new tab
			debounceTimer = setTimeout(() => {
				buildContext();
			}, 400);
		});

		observer.observe(root, {
			childList: true, // watch for added/removed nodes
			subtree: true, // watch the entire subtree
			// NOT attributes / characterData – avoids noise from style/class changes
		});

		// ── Registry subscription: rebuild context when widgets register/update ──
		// Debounced so a dashboard with 12 widgets registering on mount doesn't
		// trigger 12 rebuilds.
		let registryDebounce: ReturnType<typeof setTimeout> | null = null;
		const unsubRegistry = widgetRegistry.subscribe(() => {
			if (registryDebounce) clearTimeout(registryDebounce);
			registryDebounce = setTimeout(() => buildContext(), 200);
		});

		return () => {
			clearTimeout(timer);
			if (debounceTimer) clearTimeout(debounceTimer);
			if (registryDebounce) clearTimeout(registryDebounce);
			observer.disconnect();
			unsubRegistry();
		};
	}, [buildContext, contextSelector]);

	const refreshContext = useCallback(() => {
		setIsLoading(true);
		return buildContext();
	}, [buildContext]);

	return { context, isLoading, refreshContext };
}

// ── Exported pure functions (for testing) ─────────────────────────────────────
export { analyzeESGContext, generateSuggestedPrompts, countKeywordMatches };

// ── Serialize context for AI prompt ──────────────────────────────────────────

export function serializeContextForPrompt(context: PageContext, maxLength = 6000): string {
	const parts: string[] = [];

	parts.push(
		`## Page identity\n- URL: ${context.dom.url}\n- Title: ${context.dom.title}`
	);

	if (context.esg) {
		const e = context.esg;
		parts.push(
			`\n## Detected ESG signal\n- Domain: ${e.domain
				.replace(/_/g, " ")
				.toUpperCase()}\n- Frameworks: ${
				e.frameworks.length > 0 ? e.frameworks.join(", ") : "Not specified"
			}\n- Topics: ${
				e.topics.length > 0 ? e.topics.join(", ") : "Not specified"
			}\n- Entity: ${e.entity || "Not specified"}\n- Period: ${
				e.period || "Not specified"
			}`
		);
	}

	if (context.widgetIndex.length > 0) {
		const idxText = context.widgetIndex
			.slice(0, 50) // hard cap to keep system prompt small
			.map((w) => {
				const periodStr = w.period?.from
					? `${w.period.from}${w.period.to ? `..${w.period.to}` : ""}`
					: "";
				const fwStr = w.framework?.id
					? `${w.framework.id}${w.framework.standard ? `/${w.framework.standard}` : ""}`
					: "";
				const sizeStr = w.rowCount
					? `${w.rowCount} rows`
					: w.seriesCount
					? `${w.seriesCount} series`
					: "";
				const meta = [w.kind, w.chartLibrary, sizeStr, fwStr, w.units, periodStr]
					.filter(Boolean)
					.join(" · ");
				return `- [${w.widgetId}] "${w.title}" (${meta})`;
			})
			.join("\n");
		parts.push(
			`\n## Registered widgets on this dashboard\nUse the read_chart_data tool with widgetId to fetch full data for any of these.\n${idxText}`
		);
	}

	if (context.dom.tables.length > 0 && context.widgetIndex.length === 0) {
		// Only fall back to DOM tables when no registered widgets exist —
		// registered widgets are authoritative.
		const tableTexts = context.dom.tables
			.slice(0, 3)
			.map((t, i) => {
				const header = t.caption ? `Table ${i + 1}: ${t.caption}` : `Table ${i + 1}`;
				const headerRow = t.headers.join(" | ");
				const dataRows = t.rows
					.slice(0, 10)
					.map((r) => r.join(" | "))
					.join("\n");
				return `**${header}**\n${headerRow}\n${dataRows}`;
			})
			.join("\n\n");
		parts.push(`\n## DOM Tables (fallback)\n${tableTexts}`);
	}

	if (context.dom.headings.length > 0) {
		parts.push(`\n## Page headings\n${context.dom.headings.slice(0, 12).join("\n")}`);
	}

	if (context.apiData.length > 0) {
		const apiText = context.apiData
			.slice(-3)
			.map(
				(d) =>
					`  [${d.method}] ${d.url.slice(0, 100)}: ${JSON.stringify(d.responseData).slice(
						0,
						200
					)}`
			)
			.join("\n");
		parts.push(`\n## Recent intercepted API responses\n${apiText}`);
	}

	const inner = parts.join("\n");
	const wrapped = `${UNTRUSTED_PREAMBLE}\n\n${wrapUntrusted("page_context", inner)}`;
	return wrapped.slice(0, maxLength);
}
